/**
 * Disciplinary Hearings — `/hearings` (module 04, spec `06-hearings.md`).
 *
 * **What this page is.** The first slice of spec 06's largest module: the **case record and
 * the document pipeline**, and nothing else. §4's headline band and Case Load strip, §5's Case
 * Register with its stage key strip and competition filter, §8's Case File with the case head
 * grid and the five-step stepper, §9's three batch cards, and §14's Create New Hearing modal.
 *
 * **What this page is NOT, stated here once and again on every surface it touches.**
 *
 *  · **No `.doc` and no PDF.** §10's Hearing Letter, §11's minutes form and §12's decision
 *    letter ARE built — generated from the case record, rendered on §9's paper and printable
 *    from the browser — but no file is written and nothing is sent to anybody. The Documents
 *    panel says so where §9 would have drawn its output chips.
 *  · **No Code of Conduct catalogue.** §2 forbids hard-coding it and the `bound_instrument`
 *    store does not exist, so §14's article optgroups cannot be offered. The create modal
 *    takes the article code, the level and the quoted wording as inputs and says why.
 *  · **No evidence files, attendees, decision record, penalty or sanction tracking** and no
 *    §4 conflict delegation. Each is named where the spec puts it.
 *  · **§15's two legal clocks ARE built.** The 24-hour report window is a chip on the Case
 *    File; the appeal window is on the Decision Letter's card, derived in `@hockeytd/shared`
 *    from that letter's logged service and Configuration §05's window, and drawn as one of
 *    three honest states — served, not served, or served with no window configured.
 *
 * **Four reads here; the writes belong to the panel that makes them.** This page reads `GET
 * /hearings?competition=` for the register, `GET /hearings/:id` for the opened case, `GET
 * /configuration/code-of-conduct` once, for the Code edition §4's `Bound Instrument` line
 * names — which was a fixed string until it was read — and `GET /hearings/document-constants`
 * once, for §15's two configured thresholds. It writes `POST /hearings` and §14b's `POST
 * /hearings/:id/amendment`. Generation, the batch state moves and `PUT
 * /hearings/:id/minutes` are the Documents panel's, because §9's workspace is a sequence of
 * calls whose order only that panel knows; it tells the page one thing — something changed —
 * and both reads run again. Every route is TD-only, including the reads: a disciplinary case
 * names a player and quotes the conduct alleged against them, and there is no projection of
 * one that is safe to serve widely.
 *
 * **The stage filter runs in the browser, and the competition filter runs on the server.**
 * §5's key strip counts every stage, so narrowing the fetch to one stage would leave four
 * badges counting a set that had already been filtered to the fifth; the competition scope has
 * no such counter and is therefore the route's own parameter.
 * `CaseRegisterPanel`'s header says the same thing at more length.
 *
 * **The Case File is a panel, not a route.** §3's layout stacks it under the register on this
 * page and §5 says *"click any case to open its file below"* — so nothing is added to
 * `RESUMABLE_ROUTE_TEMPLATES`, because no new route pattern exists to add.
 */

import { useCallback, useState } from "react";

import type { HearingStage } from "@hockeytd/shared";

import {
  amendHearing,
  createHearing,
  fetchCodeOfConduct,
  fetchFixtureDays,
  fetchHearingCase,
  fetchHearingDocumentConstants,
  fetchHearings,
  fetchTeamMembers,
  fetchTeams,
} from "../api/client.js";
import type {
  CodeOfConductBody,
  CreateHearingRequest,
  FixtureListBody,
  HearingAmendmentRequest,
  HearingCaseDetailView,
  HearingDocumentConstantsBody,
  HearingListBody,
  TeamListBody,
  TeamMemberView,
} from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { usePageKicker } from "../app/pageKicker.js";
import { useTournament } from "../app/session.js";
import { useAsync } from "../app/useAsync.js";
import {
  AMEND_RECEIPT_LABEL,
  amendmentReceiptKind,
} from "../features/hearings/amendmentText.js";
import { AmendHearingModal } from "../features/hearings/AmendHearingModal.js";
import { CaseFilePanel } from "../features/hearings/CaseFilePanel.js";
import { CaseLoadPanel } from "../features/hearings/CaseLoadPanel.js";
import {
  CaseRegisterPanel,
  type CompetitionScope,
} from "../features/hearings/CaseRegisterPanel.js";
import { CreateHearingModal } from "../features/hearings/CreateHearingModal.js";
import { DocumentsPanel } from "../features/hearings/DocumentsPanel.js";

/** Spec 06's own kicker, which is longer and more exact than the module strip's default. */
export const HEARINGS_KICKER =
  "Code Of Conduct Cases · Report · Natural Justice · Hearing · Decision · Penalty";

function asError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error("The request failed.");
}

export function HearingsPage(): React.JSX.Element {
  usePageKicker(HEARINGS_KICKER);

  const [competition, setCompetition] = useState<CompetitionScope>("all");
  const [stage, setStage] = useState<HearingStage | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // One counter, bumped by every write. Both reads depend on it, so a batch move refreshes
  // the register's stage chip and the case file's stepper together — they are two views of
  // one derived value and must never disagree on screen.
  const [revision, setRevision] = useState(0);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<Error | null>(null);

  /**
   * §14b's modal, and the receipt it leaves behind.
   *
   * The receipt is held on the PAGE and not inside the modal, because the modal closes on a
   * successful save: a sentence about what became of the Hearing Letter shown inside the form
   * that is about to disappear would be a sentence nobody reads. It survives on the page until
   * the Technical Delegate moves to another case, which is the same shape the Documents panel's
   * own notice strip has.
   */
  const [amendOpen, setAmendOpen] = useState(false);
  const [amending, setAmending] = useState(false);
  const [amendError, setAmendError] = useState<Error | null>(null);
  const [amendReceipt, setAmendReceipt] = useState<{
    note: string;
    kind: "advisory" | "attention";
  } | null>(null);

  const register = useAsync<HearingListBody>(
    () => fetchHearings(competition === "all" ? {} : { competition }),
    [competition, revision],
  );

  const detail = useAsync<HearingCaseDetailView | null>(
    () => (selectedId === null ? Promise.resolve(null) : fetchHearingCase(selectedId)),
    [selectedId, revision],
  );

  /**
   * WHICH CODE EDITION THIS TOURNAMENT IS BOUND TO, for §4's `Bound Instrument` line — which
   * named the FIH Code of March 2025 in fixed text until this read existed.
   *
   * Read once, with no dependency on `revision`: `bound_instrument` is Configuration §05's
   * record and nothing on this page can move it, so re-reading it after every case write
   * would be a request per press for a value that cannot have changed.
   *
   * **A failure here is not a failure of this page.** The register, the case file and the
   * documents do not need the edition, so an error is swallowed into `null` and the line is
   * simply absent — a Technical Delegate opening a case at a venue must not be shown a red
   * banner over his register because a catalogue read timed out.
   */
  const catalogue = useAsync<CodeOfConductBody>(() => fetchCodeOfConduct(), []);
  const instrument = catalogue.status === "ready" ? catalogue.data.instrument : null;

  /**
   * §15's two configured thresholds — Configuration §05's appeal window and the 24-hour
   * report rule.
   *
   * Read once and without `revision`, for the same reason the catalogue is: nothing on this
   * page can move a Configuration item, so re-reading it after every case write would be a
   * request per press for a value that cannot have changed.
   *
   * **A failure here is `null`, and `null` is rendered as "could not be read" — never as "no
   * window is configured".** Those are two different facts with two different remedies, and
   * the panels below draw them as two. What must not happen is a red banner over a Technical
   * Delegate's register because a configuration read timed out, and what must not happen even
   * more is a countdown drawn on a guess.
   */
  const clockSettings = useAsync<HearingDocumentConstantsBody>(
    () => fetchHearingDocumentConstants(),
    [],
  );
  const clocks = clockSettings.status === "ready" ? clockSettings.data.clocks : null;
  /**
   * The default hearing venue, so the Create New Hearing form opens with the Location filled
   * in instead of asking the Technical Delegate to type his own office address every time.
   * Null when the read failed or when no venue is configured, and the form says which.
   */
  const defaultLocation =
    clockSettings.status === "ready" ? (clockSettings.data.constants?.venue ?? null) : null;

  /**
   * WHAT THE CREATE FORM OFFERS INSTEAD OF ASKING THE TD TO TYPE IT (ruling §K).
   *
   * Both are read once, without `revision`: neither a team list nor a fixture list can be
   * moved by anything on this page, so re-reading them after every case write would be two
   * requests per press for values that cannot have changed.
   *
   * **Both fail soft, into an empty list.** The form has a typed fallback on the team, on the
   * person and on the match number, and each control says on itself when the list behind it
   * could not be read. A registry this system could not reach must never be the reason a real
   * incident cannot be recorded — which is why a failure here draws no banner over the
   * register a Technical Delegate came to read.
   */
  const teamList = useAsync<TeamListBody>(() => fetchTeams(), []);
  const fixtureList = useAsync<FixtureListBody>(() => fetchFixtureDays(), []);
  const teams = teamList.status === "ready" ? teamList.data.teams : [];
  const fixtures = fixtureList.status === "ready" ? fixtureList.data.fixtures : [];

  /**
   * One team's squad, read when the form has a team AND a role to ask about. It is a callback
   * rather than a page-level fetch because which team is being asked about is the form's
   * state, not the page's — and the form drops the answer when the TD changes team.
   */
  const loadMembers = useCallback(
    async (competition: "1916" | "1917", teamCode: string): Promise<readonly TeamMemberView[]> =>
      (await fetchTeamMembers(competition, teamCode)).members,
    [],
  );

  const tournament = useTournament();

  const hearings = register.status === "ready" ? register.data.hearings : [];
  const openCase = detail.status === "ready" ? detail.data : null;

  // The Documents panel owns its own writes — generation, the state moves and the minutes —
  // because §9's workspace is a sequence of calls that only it knows the order of. All it
  // needs from the page is this: when something has changed, read both projections again.
  const bumpRevision = useCallback(() => {
    setRevision((count) => count + 1);
  }, []);

  const create = useCallback((input: CreateHearingRequest) => {
    setCreating(true);
    setCreateError(null);
    createHearing(input).then(
      (created) => {
        setCreating(false);
        setModalOpen(false);
        // §14: the stage filter resets and the new case is selected. The competition scope is
        // moved to the created case's own, so a case created on the women's competition while
        // the men's tab was showing is not created into an invisible register.
        setStage(null);
        setCompetition(created.competitionId);
        setSelectedId(created.id);
        setRevision((count) => count + 1);
      },
      (cause: unknown) => {
        setCreating(false);
        setCreateError(asError(cause));
      },
    );
  }, []);

  /**
   * §14b's save.
   *
   * The modal closes only on success, as the create modal's does, so a refused amendment leaves
   * every field the Technical Delegate typed exactly where it was with the server's own sentence
   * beside it. The receipt is the server's `letterNote`, printed verbatim: it is the one
   * statement on this page that says whether the document that has already gone out still
   * agrees with the record, and a sentence composed here could be wrong about it.
   */
  const amend = useCallback(
    (id: string, input: HearingAmendmentRequest) => {
      setAmending(true);
      setAmendError(null);
      amendHearing(id, input).then(
        (result) => {
          setAmending(false);
          setAmendOpen(false);
          setAmendReceipt({
            note: result.letterNote,
            kind: amendmentReceiptKind(result.letterAction),
          });
          setRevision((count) => count + 1);
        },
        (cause: unknown) => {
          setAmending(false);
          setAmendError(asError(cause));
        },
      );
    },
    [],
  );

  return (
    <>
      {register.status === "loading" ? <Skeleton rows={6} /> : null}
      {register.status === "error" ? <FailBanner error={register.error} /> : null}

      {register.status === "ready" ? (
        <>
          <CaseLoadPanel
            hearings={hearings}
            tournamentName={tournament?.shortDisplayName ?? null}
            instrument={instrument}
            titleAction={
              <HowToUse id="hearings" title="How to use Disciplinary Hearings">
                <li>
                  <HowToLink to="case-load">Case Load</HowToLink> counts the cases at this
                  tournament. Every figure on it is computed from the register below, so the
                  two cannot disagree.
                </li>
                <li>
                  <HowToLink to="case-register">Case Register</HowToLink> lists every case and
                  the stage it has reached. The stage chips above the list narrow it and change
                  nothing.
                </li>
                <li>Click any row to open that case in the panels below.</li>
                <li>
                  <HowToLink to="case-file">Case File</HowToLink> walks the five steps of a
                  hearing — case created, hearing letter, hearing held, decision issued, penalty
                  tracked.
                </li>
                <li>
                  <HowToLink to="documents">Documents</HowToLink> holds the letters and the
                  minutes. <b>Open Letter</b> prints from the browser; nothing is sent to
                  anybody, and the screen does not claim otherwise.
                </li>
                <li>
                  <b>The order a case runs in:</b> create the case → <b>Draft Hearing Letter</b>{" "}
                  → <b>Issue</b> → <b>Record service</b> → hold the hearing → <b>Minutes</b> →{" "}
                  <b>Decision Letter</b> → <b>Issue</b> → <b>Record service</b>, and the appeal
                  clock starts at that last one.
                </li>
                <li>
                  <b>Service</b> means recording when a letter was physically handed to the
                  Team Manager and who signed for it. It can only be recorded after the letter
                  has been issued, because a letter that has not gone out has not been served —
                  so <b>Record service</b> stays greyed out, with the reason on it, until then.
                </li>
                <li>
                  Under a decision letter, the appeal window is counted from the recorded
                  service time. Press <b>Correct Service</b> to change that time; it asks for a
                  reason. Where this server keeps corrections, each letter lists them under{" "}
                  <b>Service History</b>, with the time each one replaced and the reason given.
                </li>
                <li>
                  <b>If a hearing moves</b> — a new time, a different room — press{" "}
                  <b>Amend Hearing Details</b> on the case file rather than editing the letter
                  by hand. It asks why, and the reason is kept with the case. The record then
                  carries the new time everywhere it is counted, and the case file lists every
                  move under <b>Amendment History</b>. Once the hearing has been held the
                  button is greyed out, with the reason on it.
                </li>
                <li>
                  After an amendment the page says <b>what became of the Hearing Letter</b>. A
                  letter you have already issued is not touched: reopen it, re-draft it from
                  the case, issue it again — and if it had been served, serve the new one and
                  record that.
                </li>
                <li>
                  <HowToLink to="create-hearing">Create New Hearing</HowToLink> opens a case.
                  Team, person, role and match number are dropdowns, each with a
                  type-by-hand way out, and nothing is written until <b>Create Case</b>.
                </li>
                <li>
                  Choosing a team fills in that team&rsquo;s <b>Team Manager</b>, because the
                  Hearing Letter is addressed to them. A case that names nobody can still be
                  opened — name them later on the case file, and the letter can then be
                  drafted.
                </li>
              </HowToUse>
            }
          />

          <CaseRegisterPanel
            hearings={hearings}
            stage={stage}
            onSelectStage={setStage}
            competition={competition}
            onSelectCompetition={(next) => {
              setCompetition(next);
              setSelectedId(null);
            }}
            selectedId={selectedId}
            onSelectCase={(id) => {
              setSelectedId(id);
            }}
            onCreate={() => {
              setCreateError(null);
              setModalOpen(true);
            }}
          />

          {detail.status === "error" ? <FailBanner error={detail.error} /> : null}
          {selectedId !== null && detail.status === "loading" ? <Skeleton rows={4} /> : null}

          {/* §14b's receipt. Drawn above the case file, where the amended arrangements are, and
              in the notice idiom the Documents panel uses for the same kind of statement. */}
          {amendReceipt === null ? null : (
            <div
              className={`warnnote warnnote--${amendReceipt.kind}`}
              role="status"
              data-testid="amendReceipt"
              style={{ marginTop: 18 }}
            >
              <span className="warnnote__kind">{AMEND_RECEIPT_LABEL}</span>
              <span className="warnnote__msg">{amendReceipt.note}</span>
            </div>
          )}

          <CaseFilePanel
            hearing={openCase}
            clocks={clocks}
            onChanged={bumpRevision}
            onAmend={() => {
              setAmendError(null);
              setAmendReceipt(null);
              setAmendOpen(true);
            }}
          />
          <DocumentsPanel hearing={openCase} clocks={clocks} onChanged={bumpRevision} />
        </>
      ) : null}

      {modalOpen ? (
        <CreateHearingModal
          onCancel={() => {
            setModalOpen(false);
          }}
          onCreate={create}
          busy={creating}
          error={createError}
          teams={teams}
          fixtures={fixtures}
          defaultLocation={defaultLocation}
          loadMembers={loadMembers}
        />
      ) : null}

      {/* §14b. Drawn only with a case open, because the amendment is OF a case — the modal is
          pre-filled from the record and there is nothing to pre-fill it from otherwise. */}
      {amendOpen && openCase !== null ? (
        <AmendHearingModal
          hearing={openCase}
          onCancel={() => {
            setAmendOpen(false);
          }}
          onSave={(input) => {
            amend(openCase.id, input);
          }}
          busy={amending}
          error={amendError}
        />
      ) : null}
    </>
  );
}
